import React, { memo, useState } from "react";
import { Link, useStaticQuery, graphql } from "gatsby";
import PropTypes from "prop-types";
import styled from "styled-components";
import Logo from "../images/logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import Menu from "./menu";
import MenuSideNav from "./menu-sidenav";
import Image from "./image";
import { HiddenOnDesktop, HiddenOnPhone } from "./hidden";

const WidthWrapper = styled.div`
  max-width: 1920px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-flow: row wrap;
  height: auto;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const InnerWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 1rem;
  }
`;

const HeaderWrapper = styled.nav`
  position: sticky;
  top: 0;
  left: 0;
  color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
  background-color: ${({ theme }) =>
    theme.colors.headerBackground || theme.colors.secondary};
  height: auto;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.1);
  z-index: 999;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 0.5rem 1rem;
  }
`;
const LeftWrapper = styled.div`
  display: flex;

  a {
    display: flex;
    align-items: center;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 100%;
    justify-content: space-between;
    align-items: center;
  }
`;
const LogoImage = styled(Image)`
  object-fit: contain;
  margin-left: 40px;
  height: 45px;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin-left: 0;
  }
`;

const SvgLogo = styled.img`
  object-fit: contain;
  margin-left: 40px;
  height: 45px;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin-left: 0;
  }
`;

const StaticLogo = styled.img`
  height: 45px;
  object-fit: contain;
`;

const Burger = styled(FontAwesomeIcon)`
  margin-left: 15px;
  padding: 10px;
  cursor: pointer;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin: 0;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding: 0;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  transition: all 0.4s ease-in-out;
  opacity: ${({ $burgerOpen }) => ($burgerOpen ? "1" : "0")};
  visibility: ${({ $burgerOpen }) => ($burgerOpen ? "visible" : "hidden")};
`;

const Header = ({ staticQueryData, children }) => {
  const [burgerOpen, setBurgerOpen] = useState(undefined);
  const { wp, allWpProductCategory, headerNav } =
    staticQueryData ||
    useStaticQuery(graphql`
      fragment HeaderNav on WpMenuItem {
        order
        id
        url
        cssClasses
        label
        description
        parentId
      }
      query HeaderQuery {
        wp {
          crbThemeOptions {
            crbLogoHeader {
              media {
                mimeType
                altText
                localFile {
                  publicURL
                  childImageSharp {
                    gatsbyImageData(
                      height: 45
                      layout: FIXED
                      placeholder: NONE
                      transformOptions: { fit: CONTAIN }
                    )
                  }
                }
              }
            }
            crbLogoMobileHeader {
              media {
                mimeType
                altText
                localFile {
                  publicURL
                  childImageSharp {
                    gatsbyImageData(
                      height: 45
                      placeholder: NONE
                      transformOptions: { fit: CONTAIN }
                    )
                  }
                }
              }
            }
            crbMenuImage {
              media {
                mimeType
                altText
                localFile {
                  publicURL
                  childImageSharp {
                    gatsbyImageData(
                      height: 45
                      layout: FIXED
                      placeholder: NONE
                      transformOptions: { fit: CONTAIN }
                    )
                  }
                }
              }
            }
            crbSearchbarEnabled
            crbPaddingBottom
            crbPaddingLeft
            crbPaddingRight
            crbPaddingTop
            crbPhone
          }
          wcSettings {
            cartUrl
          }
        }
        allWpProductCategory {
          nodes {
            uri
            name
          }
        }
        headerNav: wpMenu(locations: { eq: HEADER_NAV }) {
          menuItems {
            nodes {
              childItems {
                nodes {
                  childItems {
                    nodes {
                      ...HeaderNav
                      childItems {
                        nodes {
                          ...HeaderNav
                          childItems {
                            nodes {
                              ...HeaderNav
                            }
                          }
                        }
                      }
                    }
                  }
                  ...HeaderNav
                }
              }
              parentId
              ...HeaderNav
            }
          }
        }
      }
    `);

  return (
    <>
      <HeaderWrapper>
        {children}
        <WidthWrapper>
          <InnerWrapper
            style={{
              paddingTop: `${parseInt(wp?.crbThemeOptions?.crbPaddingTop) || 0}px`,
              paddingRight: `${parseInt(wp?.crbThemeOptions?.crbPaddingRight) || 0}px`,
              paddingBottom: `${parseInt(wp?.crbThemeOptions?.crbPaddingBottom) || 0}px`,
              paddingLeft: `${parseInt(wp?.crbThemeOptions?.crbPaddingLeft) || 0}px`,
            }}
          >
            <LeftWrapper>
              <HiddenOnPhone>
                <Burger
                  icon={faBars}
                  onClick={() => setBurgerOpen(!burgerOpen)}
                  size="2x"
                />
                <Link to="/" aria-label="gå til hjemmesiden">
                  {wp.crbThemeOptions.crbLogoHeader?.media?.mimeType ==
                  "image/svg+xml" ? (
                    <SvgLogo
                      src={
                        wp.crbThemeOptions.crbLogoHeader?.media?.localFile
                          ?.publicURL
                      }
                      alt="Logotype"
                      loading="eager"
                    />
                  ) : wp?.crbThemeOptions?.crbLogoHeader?.media ? (
                    <LogoImage
                      alt="Logotype"
                      image={wp?.crbThemeOptions?.crbLogoHeader?.media}
                      loading="eager"
                    />
                  ) : (
                    <StaticLogo alt="Logotype" src={Logo} loading="eager" />
                  )}
                </Link>
              </HiddenOnPhone>
              <HiddenOnDesktop>
                <Link to="/" aria-label="gå til hjemmesiden">
                  {wp.crbThemeOptions.crbLogoMobileHeader?.media?.mimeType ==
                  "image/svg+xml" ? (
                    <SvgLogo
                      src={
                        wp.crbThemeOptions.crbLogoMobileHeader?.media?.localFile
                          ?.publicURL
                      }
                      alt="Logotype"
                      loading="eager"
                    />
                  ) : wp?.crbThemeOptions?.crbLogoMobileHeader?.media ? (
                    <LogoImage
                      alt="Logotype"
                      image={wp?.crbThemeOptions?.crbLogoMobileHeader?.media}
                      loading="eager"
                    />
                  ) : (
                    <StaticLogo alt="Logotype" src={Logo} loading="eager" />
                  )}
                </Link>
                <Burger
                  icon={faBars}
                  onClick={() => setBurgerOpen(!burgerOpen)}
                  size="2x"
                />
              </HiddenOnDesktop>
            </LeftWrapper>
            <Menu
              cartUrl={wp.wcSettings?.cartUrl}
              headerNav={headerNav}
              setBurgerOpen={setBurgerOpen}
              phoneNumber={wp.crbThemeOptions.crbPhone}
            />
          </InnerWrapper>
        </WidthWrapper>
      </HeaderWrapper>
      <MenuSideNav
        setBurgerOpen={setBurgerOpen}
        burgerOpen={burgerOpen}
        headerNav={headerNav}
        wp={wp}
        productCategories={allWpProductCategory.nodes.filter(
          (item) => item.name !== "Uncategorized"
        )}
      />
      <Backdrop
        $burgerOpen={burgerOpen}
        onClick={() => setBurgerOpen(undefined)}
      />
    </>
  );
};

Header.propTypes = {
  staticQueryData: PropTypes.object,
  children: PropTypes.node,
  dropdownOpen: PropTypes.bool,
  staticSearchData: PropTypes.object,
};

export default memo(Header);
